import { createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import Loader from 'components/Loader/Loader.component';
import { useEffect } from 'react';
import { useStore } from 'react-redux';
import apiRequest from 'services/api/api.config';
import { getCommonHeaders } from 'services/api/api.helpers';
import { checkTokenEndpoint } from 'services/oauth/oauth.config';
import { CheckTokenResponse } from 'services/oauth/oauth.types';
import { authTokenKey } from 'services/session/session.config';
import { AuthType, setAuthType } from 'services/session/session.helpers';
import { AppChannel } from 'utils/config.config';
import { z } from 'zod';

const checkTokenSearchSchema = z.object({
  callbackurl: z.string().optional(),
  siteid: z.number().optional(),
});

export const Route = createFileRoute('/check-token')({
  component: CheckToken,
  validateSearch: zodValidator(checkTokenSearchSchema),
});

function CheckToken() {
  const { getState } = useStore();

  const searchParams = Route.useSearch();

  const callbackurl = searchParams.callbackurl ?? '/private-area';
  const siteid = searchParams.siteid ?? AppChannel.ClientDesktopSisalPoker;

  useEffect(() => {
    const getAuthTokenViaPostMessage = async (event: MessageEvent) => {
      const accessToken = event.data.access_token;
      if (accessToken == null) return;

      try {
        const response = await apiRequest.request<CheckTokenResponse>({
          url: checkTokenEndpoint,
          headers: {
            ...getCommonHeaders(getState()),
            Authorization: `Bearer ${accessToken}`,
          },
        });

        if (response.data.status !== 'SUCCESS' || !response.data.jwt) {
          console.error('Error checking token, /check-token failed');
          return;
        }

        window.localStorage.setItem(authTokenKey, response.data.jwt);
        setAuthType(AuthType.Sso);

        const url = new URL(callbackurl, window.location.origin);
        url.searchParams.set('siteid', siteid.toString());
        url.searchParams.set('from-oauth', 'true');

        window.location.assign(url);
      } catch (error) {
        console.error('Error checking token', error);
      }
    };

    window.addEventListener('message', getAuthTokenViaPostMessage);
    window.parent.postMessage('loaded', '*');

    return () => {
      window.removeEventListener('message', getAuthTokenViaPostMessage);
    };
  }, [callbackurl, getState, siteid]);

  return <Loader overlayViewMode="fullscreen" active={true} />;
}
